// ============================================================================
// components/reusable/Interactive/DataGridSimple.jsx
// -----------------------------------------------------------------------------
// Tableau simple avec tri et sélection optionnelle
// (sans filtre interne, ni alternance de couleur entre lignes)
// ============================================================================

import React, { useState, useMemo } from "react";
import theme from "../../../styles/theme";

// ============================================================================
// 🔸 Composant principal
// ============================================================================
const DataGridSimple = ({
  columns = [],
  data = [],
  selectable = false,
  selectedId = null,
  onSelectionChange = () => {},
}) => {
  // --------------------------------------------------------------------------
  // 🧩 États internes
  // --------------------------------------------------------------------------
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [sort, setSort] = useState({ key: null, dir: "asc" });

  // --------------------------------------------------------------------------
  // 🔹 Tri des données
  // --------------------------------------------------------------------------
  const sortedData = useMemo(() => {
    if (!sort.key) return data;
    const dir = sort.dir === "asc" ? 1 : -1;

    return [...data].sort((a, b) => {
      const va = a[sort.key];
      const vb = b[sort.key];
      if (va == null && vb == null) return 0;
      if (va == null) return -1 * dir;
      if (vb == null) return 1 * dir;

      const na = Number(va);
      const nb = Number(vb);
      const bothNum = !Number.isNaN(na) && !Number.isNaN(nb);

      return bothNum
        ? (na - nb) * dir
        : String(va).localeCompare(String(vb), undefined, {
            sensitivity: "base",
          }) * dir;
    });
  }, [data, sort]);

  // --------------------------------------------------------------------------
  // 🔹 Rendu du tableau
  // --------------------------------------------------------------------------
  return (
    <div style={{ fontFamily: theme.fonts.primary }}>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          border: theme.borders.width.thin,
          backgroundColor: theme.colors.background.surface,
        }}
      >
        {/* --------------------------------------------------------------- */}
        {/* En-tête */}
        {/* --------------------------------------------------------------- */}
        <thead>
          <tr
            style={{
              backgroundColor: theme.colors.background.menulist,
              textAlign: "left",
            }}
          >
            {selectable && (
              <th
                style={{
                  width: 36,
                  padding: "8px 12px",
                  borderBottom: theme.borders.width.thick,
                }}
              />
            )}

            {columns.map((col) => {
              const sortable = !!col.accessor;
              const active = sort.key === col.accessor;
              const indicator = active
                ? sort.dir === "asc"
                  ? " ▲"
                  : " ▼"
                : "";

              return (
                <th
                  key={col.accessor || col.header}
                  onClick={() => {
                    if (!sortable) return;
                    setSort((s) =>
                      s.key === col.accessor
                        ? { key: s.key, dir: s.dir === "asc" ? "desc" : "asc" }
                        : { key: col.accessor, dir: "asc" }
                    );
                  }}
                  style={{
                    padding: "8px 12px",
                    borderBottom: theme.borders.width.thick,
                    fontWeight: theme.fontWeights.semiBold,
                    fontFamily: theme.fonts.heading,
                    fontSize: theme.fontSizes.md,
                    color: theme.colors.text.primary,
                    whiteSpace: "nowrap",
                    cursor: sortable ? "pointer" : "default",
                    userSelect: "none",
                  }}
                >
                  {col.header}
                  {indicator}
                </th>
              );
            })}
          </tr>
        </thead>

        {/* --------------------------------------------------------------- */}
        {/* Corps du tableau */}
        {/* --------------------------------------------------------------- */}
        <tbody>
          {sortedData.map((row, idx) => {
            const id = row.id ?? idx;
            const isSelected = selectedId === id;
            const isHovered = hoveredIdx === idx;

            return (
              <tr
                key={id}
                onMouseEnter={() => setHoveredIdx(idx)}
                onMouseLeave={() => setHoveredIdx(null)}
                onClick={() => {
                  if (!selectable) return;
                  onSelectionChange(
                    isSelected ? null : id,
                    isSelected ? null : row
                  );
                }}
                style={{
                  backgroundColor: isHovered
                    ? theme.colors.background.hover
                    : theme.colors.background.surface, // ✅ pas de couleur alternée
                  cursor: selectable ? "pointer" : "default",
                }}
              >
                {selectable && (
                  <td
                    style={{
                      padding: "8px 12px",
                      borderBottom: theme.borders.width.thin,
                      verticalAlign: "middle",
                      width: 36,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() =>
                        onSelectionChange(
                          isSelected ? null : id,
                          isSelected ? null : row
                        )
                      }
                      aria-label="Sélectionner la ligne"
                    />
                  </td>
                )}

                {columns.map((col) => (
                  <td
                    key={col.accessor || col.header}
                    style={{
                      padding: "8px 12px",
                      borderBottom: theme.borders.width.thin,
                      color: theme.colors.text.body,
                      fontSize: theme.fontSizes.md,
                    }}
                  >
                    {col.render ? col.render(row) : row[col.accessor]}
                  </td>
                ))}
              </tr>
            );
          })}

          {/* --------------------------------------------------------------- */}
          {/* Ligne vide si aucun résultat */}
          {/* --------------------------------------------------------------- */}
          {sortedData.length === 0 && (
            <tr>
              <td
                colSpan={columns.length + (selectable ? 1 : 0)}
                style={{
                  padding: "12px",
                  textAlign: "center",
                  color: theme.colors.text.datagridColumn,
                  fontStyle: "italic",
                }}
              >
                Aucun résultat
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

// ============================================================================
export default DataGridSimple;
